import { ArrowRight, Info, Loader2 } from "lucide-react";
import { Button } from "./button";
import { Card, CardBody, CardHeader } from "./card";
import { cn } from "./cn";
import { Field } from "./field";
import { Notice } from "./notice";

const PRESETS = [10, 25, 50, 100];

/** The largest single top-up the form will submit, in whole currency units. */
export const MAX_TOP_UP = 5000;

/**
 * Validation the panel draws for itself: the shape of the amount, not whether
 * the platform will accept it. Exported so the screen can gate its own submit
 * on the same rule instead of re-deriving it.
 */
export function topUpAmountError(amount: string): string | undefined {
  if (amount.trim() === "") return undefined;
  const value = Number(amount);
  if (!Number.isFinite(value) || value <= 0) return "Enter an amount greater than zero.";
  if (value > MAX_TOP_UP) return `The maximum single top-up is USD ${MAX_TOP_UP.toLocaleString("en-US")}.`;
  return undefined;
}

/**
 * The top-up form's chrome — presets, amount field, submit button.
 *
 * The design owned the amount in a `useState` and faked the checkout with a
 * `setTimeout`. Here the amount is CONTROLLED and submission is a callback:
 * the real flow mints a checkout session on the platform and then renders its
 * URL as a link the customer presses — the portal navigates nowhere on the
 * strength of a response body — which is the screen's job, not a component's.
 * `status` and `submitError` are what the screen reports back.
 */
export function AddCreditPanel({
  appName,
  amount,
  onAmountChange,
  onSubmit,
  status = "idle",
  submitError,
  currency = "USD",
}: {
  appName: string;
  amount: string;
  onAmountChange: (amount: string) => void;
  onSubmit: () => void;
  status?: "idle" | "loading" | "done";
  /** A failure from the platform, shown under the amount like a field error. */
  submitError?: string;
  currency?: string;
}) {
  const error = submitError ?? topUpAmountError(amount);
  const disabled = !amount.trim() || Boolean(error) || status !== "idle";

  return (
    <Card>
      <CardHeader title="Add credit" description={`Tops up the ${appName} wallet only.`} />
      <CardBody className="space-y-5">
        <Notice
          title="Credits belong to one application"
          icon={<Info className="size-4" aria-hidden />}
        >
          Credit added here can only be spent in {appName}. It is never transferred to
          another application, and is not refundable.
        </Notice>

        <div className="flex flex-wrap gap-2">
          {PRESETS.map((preset) => (
            <button
              key={preset}
              type="button"
              onClick={() => onAmountChange(String(preset))}
              aria-pressed={amount === String(preset)}
              className={cn(
                "tnum rounded-pill border px-3 py-1.5 text-xs font-semibold transition-colors",
                amount === String(preset)
                  ? "border-brand bg-brand-soft text-brand-ink"
                  : "border-line text-ink-3 hover:border-ink-4/40 hover:text-ink",
              )}
            >
              {currency} {preset}
            </button>
          ))}
        </div>

        <Field
          label="Amount"
          inputMode="decimal"
          value={amount}
          onChange={(event) => onAmountChange(event.target.value)}
          suffix={currency}
          error={error}
          hint="In whole currency units — 25 means 25.00."
          inputClassName="tnum font-display text-lg font-bold"
        />

        <Button className="w-full" size="lg" disabled={disabled} onClick={onSubmit}>
          {status === "loading" ? (
            <>
              <Loader2 className="size-4 animate-spin" aria-hidden />
              Opening checkout…
            </>
          ) : status === "done" ? (
            "Checkout opened"
          ) : (
            <>
              Continue to payment
              <ArrowRight className="size-4" aria-hidden />
            </>
          )}
        </Button>

        <p className="text-center text-2xs text-ink-4" role="status">
          You will be taken to the payment provider.
        </p>
      </CardBody>
    </Card>
  );
}
